import { useCallback, useMemo } from "react";
import { Location } from "@/types";
import {
  useUnifiedNewsStore,
  useCurrentLocation,
} from "@/stores/unified-news-store";
import { useQueryClient } from "@tanstack/react-query";

interface UseUnifiedLocationFilterOptions {
  syncWithUrl?: boolean;
  autoInvalidateQueries?: boolean;
}

interface UseUnifiedLocationFilterReturn {
  currentLocation: Location | null;
  currentLocationSlug: string | null;
  setLocation: (location: Location | null) => void;
  clearLocation: () => void;
}

/**
 * Simplified location filter hook that works with TanStack Query
 */
export const useUnifiedLocationFilter = ({
  syncWithUrl = true,
  autoInvalidateQueries = false, // EMERGENCY FIX: Disabled to prevent infinite loop
}: UseUnifiedLocationFilterOptions = {}): UseUnifiedLocationFilterReturn => {
  const queryClient = useQueryClient();
  const currentLocation = useCurrentLocation();
  const setLocationInStore = useUnifiedNewsStore((state) => state.setLocation);

  // Get current location slug
  const currentLocationSlug = useMemo(() => {
    return currentLocation?.slug || null;
  }, [currentLocation]);

  // Set location with automatic query invalidation
  const setLocation = useCallback(
    (location: Location | null) => {
      // Update store
      setLocationInStore(location);

      // EMERGENCY FIX: Temporarily disable auto invalidation to prevent infinite loop
      // TODO: Re-enable with proper debouncing after fixing the infinite loop
      // if (autoInvalidateQueries) {
      //   queryClient.invalidateQueries({ queryKey: ["news"] });
      //   queryClient.invalidateQueries({ queryKey: ["homepage"] });
      // }

      // Update URL if needed
      if (syncWithUrl && typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (location?.slug) {
          url.searchParams.set("l", location.slug);
        } else {
          url.searchParams.delete("l");
        }

        // Use pushState to avoid full page reload
        window.history.pushState({}, "", url.toString());
      }
    },
    [setLocationInStore, queryClient, autoInvalidateQueries, syncWithUrl],
  );

  // Clear location
  const clearLocation = useCallback(() => {
    setLocation(null);
  }, [setLocation]);

  return {
    currentLocation,
    currentLocationSlug,
    setLocation,
    clearLocation,
  };
};

/**
 * Hook specifically for homepage location filtering
 */
export const useHomepageLocationFilter = () => {
  return useUnifiedLocationFilter({
    syncWithUrl: true,
    autoInvalidateQueries: false, // EMERGENCY FIX: Disabled to prevent infinite loop
  });
};

/**
 * Hook for getting location from URL parameters
 */
export const useLocationFromUrl = (): string | null => {
  return useMemo(() => {
    if (typeof window === "undefined") return null;

    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("l");
  }, []);
};

/**
 * Hook for initializing location from URL on mount
 */
export const useInitializeLocationFromUrl = () => {
  const locationSlugFromUrl = useLocationFromUrl();
  const { setLocation } = useUnifiedLocationFilter({ syncWithUrl: false });

  // This would typically be called in a useEffect in your main component
  const initializeFromUrl = useCallback(
    (availableLocations: Location[]) => {
      if (locationSlugFromUrl) {
        const location = availableLocations.find(
          (loc) => loc.slug === locationSlugFromUrl,
        );
        if (location) {
          setLocation(location);
        }
      }
    },
    [locationSlugFromUrl, setLocation],
  );

  return { initializeFromUrl, locationSlugFromUrl };
};

export default useUnifiedLocationFilter;
